import { LayoutGenerationRequest, LayoutGenerationResponse } from "@/types/api.type";
import ErrorService, { error } from "../error/error.service";
import HttpClientService from "./http-client/http-client.service";

export class ApiService {
    private static _instance: ApiService | undefined = undefined;
    private _httpClientService: HttpClientService;

    /**
     * Private constructor to enforce singleton pattern.
     * @param error - The error service instance
     * @param httpClient - The HTTP client service instance
     */
    private constructor(httpClient: HttpClientService) {
        this._httpClientService = httpClient;
    }

    /**
     * Initializes the singleton instance of ApiService.
     * @param baseUrl - The base URL for the API
     * @param errorService - The error service instance
     * @param httpClientService - The HTTP client service instance
     */
    public static initialize(
        baseUrl: string = import.meta.env.VITE_API_BASE_URL,
        errorService: ErrorService = error,
        httpClientService: HttpClientService = new HttpClientService(baseUrl, errorService),
    ): void {
        if (!this._instance) {
            this._instance = new ApiService(httpClientService);
        }
    }

    /**
     * Gets the singleton instance of ApiService.
     * Throws an error if the service is not initialized.
     */
    public static get instance(): ApiService {
        if (!this._instance) {
            throw new Error("ApiService is not initialized. Please call initialize() first.");
        }
        return this._instance;
    }

    /**
     * Generate layout using the backend API
     * @param request - Layout generation request payload
     * @returns Promise with layout generation response
     */
    async generate(request: LayoutGenerationRequest): Promise<LayoutGenerationResponse> {
        return await this._httpClientService.post<LayoutGenerationResponse, LayoutGenerationRequest>(
            "/process/generate",
            request,
        );
    }
}

ApiService.initialize();
export const api = ApiService.instance;
export default ApiService;
